import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
//import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import FloatingModalPause from '@/components/templates/FloatingModalPause';
import ModalChangePassword from '@/components/templates/ModalChangePassword';
import { EAuth } from '@/config/enums';
import { setTokenData } from '@/helpers';
import { signOut as signout } from '@/helpers';
import { useCallTimer } from '@/helpers/cronometro';
import { clearDataBase, openDatabase } from '@/helpers/database-helpers';
import { isPostHogEnabled } from '@/helpers/posthog';
import phoneAgent, {
  handleDisconnectTelecom,
} from '@/hooks/socket/socketTelecom';
import { arrayAgentes } from '@/hooks/socket/useAuthSocket';
import { arrayAttendance } from '@/hooks/useAttendence';
import { useChangePassword } from '@/hooks/useChangePassword';
import { arrayChatWindowInternal } from '@/hooks/useChatInternal';
import { getDatapayloadAgente, payloadDispacth } from '@/hooks/usePayloadAgent';
import usePausedStreamSSE from '@/hooks/useSSE/usePausedStreamSSE';
import { EUserType, User } from '@/models';
import { AuthLoginResponseType } from '@/models/Auth';
import { socketEvents } from '@/models/Socket';
import { api, setAccessToken, URLCliente } from '@/services/api';
import { getLogosManagement } from '@/services/api/image';
import { getQueue } from '@/services/api/queue';
import { attendance } from '@/store/features/chatWindowSlice';
import { contactActions } from '@/store/features/contact/contactSlice';
import { auxModalActions } from '@/store/features/modal/auxModalSlice';
import { modalActions } from '@/store/features/modal/modalSlice';
import { deletePause, setPause } from '@/store/features/pauseSlice';
import {
  changePayload,
  setLoginDate,
  addSystemName,
  setURLHelpCenter,
  resetPayloadStore,
  setAllowedWhisper,
  setRequireQueueOnAttendanceStart,
} from '@/store/features/payload/payloadSlice';
import {
  choseLoader,
  extension,
  clearNotifications,
  setAllQueues,
  setLogosManagement,
  // validTelecom as valid,
} from '@/store/features/utilsVariableSlice';
import { useRouter } from 'next/router';
import { destroyCookie, setCookie } from 'nookies';
import { parseCookies } from 'nookies';
import posthog from 'posthog-js';

import { requestChannell } from './ContextAux';
import { useSocket } from './SocketContext';

export let arrayPayloadInitial: any = [];

type SignInCredentials = {
  login: string;
  senha: string;
};

type AuthContextData = {
  signIn(credentials: SignInCredentials, cb?: () => void): Promise<void>;
  isAuthenticated: boolean;
  errMessage?: string;
  setErrMessage?: Dispatch<SetStateAction<string>>;
  currentURL?: string;
  userType?: User;
  idAgente?: number;
  systemName?: string;
  link: string;
  fetchAgentDatas: () => Promise<void>;
  dispach: (data: any, systemName: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext({} as AuthContextData);
export function AuthProvider({ children }: { children: ReactNode }) {
  const cookies = parseCookies();
  const {
    connect: connectSocket,
    emit,
    disconnect: disconnectSocket,
  } = useSocket();
  const [userType, setUserType] = useState<User>();
  //const [channel, setChannel] = useState<AgentChannel | null>(null);
  const [errMessage, setErrMessage] = useState('');
  const [currentURL, setCurrentURL] = useState<string>('');
  const [idAgente, setIdAgente] = useState<number>(
    +cookies[EAuth.ID_AGENTE] ? +cookies[EAuth.ID_AGENTE] : 0,
  );
  const [systemName, setSystemName] = useState<string>(
    cookies[EAuth.SYSTEM_NAME] ? cookies[EAuth.SYSTEM_NAME] : '',
  );

  useCallTimer();

  // const { validTelecom } = useSelector((state: RootState) => state.emotions);

  const isAuthenticated = userType !== undefined;
  const router = useRouter();
  const dispatch = useDispatch();
  function adminAuth({ login, senha }: SignInCredentials, systemName: string) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `https://${window.location.hostname}:4433/${systemName}/template/login.php`;
    form.style.display = 'none';

    const loginInput = document.createElement('input');
    loginInput.type = 'hidden';
    loginInput.name = 'login';
    loginInput.value = login;

    const senhaInput = document.createElement('input');
    senhaInput.type = 'hidden';
    senhaInput.name = 'senha';
    senhaInput.value = senha;

    form.appendChild(loginInput);
    form.appendChild(senhaInput);

    document.body.appendChild(form);

    form.submit();
  }

  async function signIn({ login, senha }: SignInCredentials, cb?: () => void) {
    try {
      const response = await api.post('auth/login', {
        login,
        senha,
      });
      const data = response.data as AuthLoginResponseType;

      if (data.type == 'Admin') {
        adminAuth({ login, senha }, response.data.systemName);
        return;
      }

      setAccessToken(data.token);

      if (data.must_change_password) {
        dispatch(
          modalActions.add({
            component: ModalChangePassword,
            props: {
              page: 'login',
              reason: data.password_expired_reason,
              onConfirm: () => {
                if (cb) cb();
              },
            },
            disableBackdropClick: true,
          }),
        );
        return;
      }

      const idAgent = response.data.id_agente;
      const idClient = response.data.user_login.id_cliente;

      const sendAgentName =
        cookies[
          `${EAuth.SEND_AGENT_NAME_IN_MESSAGE}-${idAgent}-${idClient}`
        ] === undefined
          ? true
          : cookies[
              `${EAuth.SEND_AGENT_NAME_IN_MESSAGE}-${idAgent}-${idClient}`
            ] === 'true';

      await setTokenData({
        token: response.data.token,
        refreshToken: response.data.refresh_token,
        idAgente: response.data.id_agente,
        systemName: response.data.systemName,
        idCliente: response.data.user_login.id_cliente,
        isModule: response.data.cliente_mod_ipbx,
        userType: data.type,
        sendAgentName,
      });

      dispatch(deletePause());

      destroyCookie(undefined, EAuth.LAST_RELOAD_DATE);
      setCookie(
        undefined,
        EAuth.LOGIN_DATE,
        Math.floor(new Date().getTime() / 1000).toString(),
        {
          maxAge: 60 * 60 * 24 * 30,
          path: '/',
        },
      );

      dispatch(setLoginDate(Math.floor(new Date().getTime() / 1000)));

      await fetchAgentDatas(true);
    } catch (err: any) {
      const errMessage = err?.response?.data?.message;
      if (errMessage) {
        setErrMessage(errMessage);
      }
    }
  }

  async function dispach(
    data: any,
    systemName: string,
    onLogin: boolean = false,
  ) {
    await connectSocket();
    const currentURL: any = window.location.hostname;
    setCurrentURL(currentURL);
    const payload = await payloadDispacth(data, systemName, currentURL);
    dispatch(changePayload(payload));
    arrayPayloadInitial = data;
    emit(socketEvents.USER_LOGGED, {
      ...data,
      onLogin,
    });
    const { data: allQueues, error } = await getQueue();
    if (!error) dispatch(setAllQueues(allQueues));
  }

  const { checkIsExpiredThreeMonths } = useChangePassword({});

  async function fetchAgentDatas(onLogin: boolean = false) {
    const cookies = parseCookies();
    const idAgent = Number(cookies[EAuth.ID_AGENTE]);
    const token = cookies[EAuth.TOKEN_KEY];
    const type = cookies[EAuth.USER_TYPE];
    const systemName = cookies[EAuth.SYSTEM_NAME];

    api.defaults.headers['Authorization'] = `Bearer ${token}`;
    setUserType({ type: type as EUserType });
    setIdAgente(idAgent);
    setSystemName(systemName);
    dispatch(addSystemName({ systemName }));

    const data: any = await getDatapayloadAgente(idAgent);

    if (!data) return;

    if (data.pausePayload?.isPaused) {
      dispatch(setPause(data.pausePayload));

      dispatch(
        auxModalActions.addAuxModal({
          component: FloatingModalPause,
          isDraggable: true,
          disableBackdropClick: true,
        }),
      );
    }

    arrayChatWindowInternal.id_agente = idAgent;

    const cookie = parseCookies();

    if (!data.ramal && data.agente.phone) {
      const ramalCookie = cookie[EAuth.RAMAL]
        ? JSON.parse(cookie[EAuth.RAMAL])
        : null;

      const channellSelected: any = ramalCookie
        ? ramalCookie
        : await requestChannell(dispatch);

      if (!ramalCookie) {
        setCookie(undefined, EAuth.RAMAL, JSON.stringify(channellSelected), {
          maxAge: 60 * 60 * 24 * 30,
          path: '/',
        });
      }
      dispatch(extension(channellSelected.label));

      data.agente.ramal = channellSelected.label;
      data.agente.id_ramal = channellSelected.value;
      data.payloadExtension = channellSelected.payloadExtension;
      data.ramal = data.payloadExtension;
    }
    if (
      (data.ramal && data.ramal.is_web == 'yes') ||
      (data.ramal &&
        data.payloadExtension &&
        data.payloadExtension.is_web == 'yes')
    ) {
      phoneAgent({
        username: data.ramal.user_ramal
          ? data.ramal.user_ramal
          : data.payloadExtension?.user_ramal,
        server: URLCliente.URLHOST,
        port: 8090,
        password: data.ramal.secret
          ? data.ramal.secret
          : data.payloadExtension.secret,
      });
    }

    dispatch(setLogosManagement(await getLogosManagement('auth')));

    dispach(data, systemName, onLogin);

    dispatch(setURLHelpCenter({ urlHelpCenter: data?.urlHelpCenter }));

    dispatch(
      setAllowedWhisper({ allowedWhisper: data?.allowedWhisper ?? false }),
    );

    dispatch(
      setRequireQueueOnAttendanceStart({
        requireQueueOnAttendanceStart:
          data?.requireQueueOnAttendanceStart ?? false,
      }),
    );

    if (data) {
      router.push('/');
    }

    await openDatabase();

    await new Promise((resolve) => {
      setTimeout(async () => {
        resolve(await checkIsExpiredThreeMonths());
      }, 1000);
    });
  }

  async function signOut(preventRedirect: boolean = false) {
    signout(async () => {
      if (isPostHogEnabled()) {
        posthog.reset();
      }

      emit(socketEvents.USER_DESLOGED, {
        logoutType: 'explicit',
      });

      dispatch(contactActions.resetDatas());
      dispatch(auxModalActions.destroy());
      dispatch(modalActions.clear());

      dispatch(
        attendance({
          chatWindow: '',
        }),
      );

      dispatch(clearNotifications());

      arrayAttendance.length = 0;
      arrayAgentes.length = 0;
      dispatch(
        choseLoader({
          loaderType: 'loadingAttendanceList',
          value: true,
        }),
      );
      clearDataBase();
      dispatch(resetPayloadStore());
      await handleDisconnectTelecom();
      await disconnectSocket();

      if (!preventRedirect) {
        router.push('/auth/login');
      }
    });
  }

  usePausedStreamSSE();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        signIn,
        userType,
        currentURL,
        errMessage,
        setErrMessage,
        idAgente,
        systemName,
        link: 'https://' + currentURL + '/' + systemName + '/',
        fetchAgentDatas,
        dispach,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
export function useAuth(): AuthContextData {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
